import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TenantStateService } from 'src/app/shared/services/tenant.state.service';
import { Module } from 'src/app/models/module';
import { AuthService } from '../../services/auth.service';
import { TenantState } from 'src/app/store/state/tenant.state';
import { Select } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ChildModule } from 'src/app/models/childModule';
import { SpaRoutingService, sidebarMode } from 'src/app/shared/services/spa.routing.service';
import { Tenant } from 'src/app/models/tenant';
import { ConfigurationAccessService } from 'src/app/configuration/configuration-access.service';
import { CONFIGURATION_OVERVIEW, ConfigurationGroupSections, ConfigurationSection } from 'src/app/configuration/configuration-registry';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    standalone: false
})

export class SidebarComponent implements OnInit, OnDestroy {
  @Select(TenantState.getCurrentTenant) getCurrentTenant$: Observable<Tenant>;
  @Select(TenantState.getCurrentModule) getCurrentModule$: Observable<Module>;
  @Select(TenantState.getCurrentChildModule) getCurrentChildModule$: Observable<ChildModule>;
  public tenantId: string;
  public isActive = false;
  public isSelected = false;
  public currentModule: Module;
  @Input() parent: any;
  @Output() updateSize = new EventEmitter<any>();
  sidebarItems: any[];
  selectedChildModule: any;
  configMode = false;
  readonly configOverview = CONFIGURATION_OVERVIEW;
  configGroups: ConfigurationGroupSections[] = [];
  activeSectionKey: string | null = null;
  private destroy$ = new Subject();

  constructor(
    private tenantStateService: TenantStateService,
    private authService: AuthService,
    public router: Router,
    public translate: TranslateService,
    private spaRoutingService: SpaRoutingService,
    private configAccess: ConfigurationAccessService
  ) { }

  ngOnInit(): void {
    this.getCurrentTenant$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tenant => {
        if (tenant) {
          this.tenantId = tenant.externalId;
          if (this.currentModule) {
            this.populateSidebar(this.currentModule);
          }
        }
        else if (this.authService.isLoggedIn()) {
          this.tenantStateService.ensureHealthyState();
        }
      });
    this.getCurrentModule$
      .pipe(takeUntil(this.destroy$))
      .subscribe(module => {
        if (module) {
          this.populateSidebar(module);
        }
      });
    this.getCurrentChildModule$
      .pipe(takeUntil(this.destroy$))
      .subscribe(cm => {
        if (cm) {
          this.selectedChildModule = cm;
          this.selectSidebarItem();
        }
      });
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.syncActiveSection());
    this.syncActiveSection();
  }

  // get the current module and use its child module data to build the sidebar and the routes
  populateSidebar(module: Module): void {
    if (this.currentModule !== module) {
      this.selectedChildModule = null;
    }
    this.currentModule = module;
    if (this.router.url.includes("registration?code=")) {
      this.currentModule = null;
    }
    this.configMode = sidebarMode(this.currentModule) === 'registry';
    if (this.configMode) {
      this.configGroups = this.configAccess.visibleGroupSections();
      return;
    }
    // Shallow-copy the store-derived items before use: NGXS freezes state objects
    // in dev mode, so the in-place `class`/`isSelected` mutations here and in
    // selectSidebarItem would throw "object is not extensible". Copies are safe to
    // mutate and decouple the UI list from frozen state.
    this.sidebarItems = (this.currentModule?.childModules ?? []).map(c => ({ ...c }));
  }
  selectSidebarItem(){
    if (this.configMode || !this.sidebarItems) {
      return;
    }
    const isDetailVariant = this.selectedChildModule.class === 'hide';
    this.sidebarItems.forEach(i => {
      const isMatch = isDetailVariant
        ? i.link === this.selectedChildModule.link
        : this.isEqualIgnoringSelection(this.selectedChildModule, i);
      i.isSelected = isMatch;
    });
  }
  isEqual(a: any, b: any) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  // sidebarItems are mutable copies that gain an `isSelected` flag, while
  // selectedChildModule comes straight from (frozen) state without it. Compare
  // with that volatile flag stripped so the match survives once items are tagged.
  private isEqualIgnoringSelection(a: any, b: any): boolean {
    const strip = (o: any) => { const { isSelected, ...rest } = o ?? {}; return rest; };
    return this.isEqual(strip(a), strip(b));
  }
  toggleClass() {
    this.isActive = !this.isActive;
    this.updateSize.emit(this.isActive);
  }

  // handle routing from sidebar links
  async toggleClassAlink(sidebarItem: any): Promise<void> {
    this.spaRoutingService.navigateToModule(this.currentModule, sidebarItem, this.tenantId);
  }

  openConfigOverview(): void {
    this.spaRoutingService.navigateToConfigurationSection('', this.tenantId);
  }

  openConfigSection(section: ConfigurationSection): void {
    this.spaRoutingService.navigateToConfigurationSection(section.path, this.tenantId);
  }

  private syncActiveSection(): void {
    this.activeSectionKey = this.configAccess.activeSectionKey(this.router.routerState.snapshot.root);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
